<script setup lang="ts">
import {computed, inject, ref} from "vue";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import MedicationCheckupDoctorFeedbackCard from "@/medication-checkup/components/MedicationCheckupDoctorFeedbackCard.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {
    DoctorDecisionDraft,
    DoctorFeedbackView,
    DoctorInitiativeDraft
} from "@/medication-checkup/interfaces/doctor-feedback";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {namesProduct, requiresOutcome} from "@/medication-checkup/services/InterventionStatuses";
import {pluralize} from "@/core/filters/Pluralize";

/**
 * The scene of the doctor's answer: the last of the analysis, played once
 * the doctor has answered the synthesis. The pharmacist records, proposal by
 * proposal, what was decided, and the changes the doctor made of their own
 * accord.
 *
 * The table is a screen ritual: the decisions and the changes are the
 * scene's own state, and one closing action submits them whole. A row left
 * untouched writes nothing — silence is an absence, not a refusal — and the
 * validation asks for a confirmation when the doctor did not speak of
 * everything, since what they left out will not reach the posology plan.
 *
 * The block refuses to close on nothing at all: the advice interview has
 * nothing to go on until the doctor has spoken. An analysis that proposed
 * nothing is the exception: there is nothing to arbitrate, no table, and
 * the scene closes in one click.
 */

const props = defineProps<SceneProps<DoctorFeedbackView>>();

const {
    bubbleShown,
    cardShown,
    clicked,
    view,
    text,
    actions,
    closing,
    acted,
    act: submitBlock
} = useScene(props, {card: true});

const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => Promise<void>
) => Promise<void>;

const submitting = ref(false);

/**
 * What the pharmacist has to fill before validating, worded for them.
 */
const warning = ref<string | null>(null);

/**
 * What the doctor answered, by row key. A row without an entry is one they
 * said nothing about. Local until the closing action submits it, and resets
 * with the scene.
 */
const drafts = ref<Record<string, DoctorDecisionDraft>>({});

/**
 * The changes the doctor made of their own accord, keyed locally until the
 * record gives them a row.
 */
const ownChanges = ref<DoctorInitiativeDraft[]>([]);

const rows = computed(() => view.value?.rows ?? []);

/**
 * Whether the analysis proposed nothing to the doctor: the HDS serves no
 * row then, and offers to close instead of validating. A record validated
 * on a change of the doctor's own keeps its table.
 */
const withoutProposal = computed(() => {
    return Boolean(view.value) && rows.value.length === 0 && (view.value?.initiatives ?? []).length === 0;
});

const updateDraft = (key: string, draft: DoctorDecisionDraft | null) => {
    const next = {...drafts.value};

    if (draft === null) {
        delete next[key];
    } else {
        next[key] = draft;
    }

    drafts.value = next;
    warning.value = null;
};

const addInitiative = (initiative: DoctorInitiativeDraft) => {
    ownChanges.value = [...ownChanges.value, initiative];
    warning.value = null;
};

const updateInitiative = (initiative: DoctorInitiativeDraft) => {
    ownChanges.value = ownChanges.value.map(current => current.key === initiative.key ? initiative : current);
    warning.value = null;
};

const removeInitiative = (key: string) => {
    ownChanges.value = ownChanges.value.filter(initiative => initiative.key !== key);
};

/**
 * How many proposals the doctor has not spoken about: they will not reach
 * the posology plan.
 */
const unanswered = computed(() => rows.value.length - Object.keys(drafts.value).length);

/**
 * What the advice interview will go on with, as the table reads right now.
 */
const tally = computed(() => {
    if (props.frozen) {
        return view.value?.summary ?? {retained: 0, refused: 0, unanswered: 0};
    }

    const decisions = Object.values(drafts.value);
    const refused = decisions.filter(draft => draft.decision === 'refused').length;

    return {
        retained: decisions.length - refused + ownChanges.value.length,
        refused,
        unanswered: unanswered.value
    };
});

const recorded = computed(() => Object.keys(drafts.value).length + ownChanges.value.length);

/**
 * The treatments whose decision lacks what its status calls for, named so
 * the pharmacist knows where to go back to.
 */
const incomplete = computed<string[]>(() => {
    const amended = rows.value
        .filter(row => {
            const draft = drafts.value[row.key];

            if (draft === undefined || draft.decision !== 'amended') {
                return false;
            }

            return draft.status === null
                || (requiresOutcome(draft.status) && draft.outcome.trim() === '')
                || (namesProduct(draft.status) && draft.product === null);
        })
        .map(row => row.label ?? '');

    const own = ownChanges.value
        .filter(initiative => {
            return initiative.status === null
                || (requiresOutcome(initiative.status) && initiative.outcome.trim() === '');
        })
        .map(initiative => initiative.label);

    return [...amended, ...own];
});

const payload = () => ({
    decisions: rows.value
        .filter(row => drafts.value[row.key] !== undefined)
        .map(row => {
            const draft = drafts.value[row.key];
            const status = draft.decision === 'amended' ? draft.status : null;

            return {
                intervention_id: row.intervention_id,
                decision: draft.decision,
                status,
                outcome: status !== null && requiresOutcome(status) ? draft.outcome.trim() : null,
                product_id: status !== null && namesProduct(status) ? draft.product?.id ?? null : null,
                comment: draft.comment.trim() || null
            };
        }),
    initiatives: ownChanges.value.map(initiative => ({
        intervention_id: initiative.intervention_id,
        status: initiative.status,
        outcome: initiative.status !== null && requiresOutcome(initiative.status)
            ? initiative.outcome.trim()
            : null,
        product_id: initiative.status !== null && namesProduct(initiative.status)
            ? initiative.product?.id ?? null
            : null,
        comment: initiative.comment.trim() || null
    }))
});

const submit = async (action: MedicationCheckupAction) => {
    submitting.value = true;

    try {
        await submitBlock(action, withoutProposal.value ? {} : payload());
    } finally {
        submitting.value = false;
    }
};

/**
 * Take the closing action: refused while nothing is recorded or a decision
 * lacks its words, confirmed on what the doctor left unsaid, since those
 * proposals will not reach the posology plan.
 */
const act = async (action: MedicationCheckupAction) => {
    if (withoutProposal.value) {
        await submit(action);

        return;
    }

    if (recorded.value === 0) {
        warning.value = "Aucun retour du médecin n'a encore été consigné.";

        return;
    }

    if (incomplete.value.length > 0) {
        warning.value = `Renseignez la décision du médecin pour : ${incomplete.value.join(', ')}.`;

        return;
    }

    if (unanswered.value > 0) {
        const count = unanswered.value;

        return openConfirmationModal(
            'Valider avec des propositions sans réponse ?',
            `Le médecin ne s'est pas prononcé sur ${count} ${pluralize('proposition', count)} : `
            + "elle n'entrera pas au plan de posologie de l'entretien conseil. "
            + 'Une fois validé, le tableau ne pourra plus être modifié.',
            () => submit(action)
        );
    }

    await submit(action);
};
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupDoctorFeedbackCard
        v-if="view && !withoutProposal"
        :rows="view.rows"
        :addable="view.addable"
        :initiatives="view.initiatives"
        :decisions="view.decisions"
        :statuses="view.statuses"
        frozen
      />
      <span
        v-if="!withoutProposal"
        class="ml-10 text-xs font-semibold text-primary"
      >
        {{ tally.retained }} {{ pluralize('retenue', tally.retained) }}
        · {{ tally.refused }} {{ pluralize('écartée', tally.refused) }}
        · {{ tally.unanswered }} sans réponse
      </span>
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
      <MedicationCheckupCopiloteLine v-if="closing" :text="closing" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupDoctorFeedbackCard
        v-if="cardShown && view && !withoutProposal"
        :rows="view.rows"
        :addable="view.addable"
        :decisions="view.decisions"
        :statuses="view.statuses"
        :drafts="drafts"
        :own-changes="ownChanges"
        :busy="submitting"
        class="animate-message-in"
        @update:draft="updateDraft"
        @add="addInitiative"
        @update:initiative="updateInitiative"
        @remove-initiative="removeInitiative"
      />

      <span
        v-if="cardShown && recorded > 0"
        class="ml-10 text-xs font-semibold text-primary animate-message-in"
      >
        {{ tally.retained }} {{ pluralize('retenue', tally.retained) }}
        · {{ tally.refused }} {{ pluralize('écartée', tally.refused) }}
        · {{ tally.unanswered }} sans réponse
      </span>

      <span
        v-if="warning"
        class="ml-10 text-xs font-semibold text-red-light animate-message-in"
      >
        {{ warning }}
      </span>

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        @act="act"
      />
    </template>
  </div>
</template>
